import { isNonNaNNumber } from './Validation'

const millisecond = 1
const second = 1000 * millisecond
const minute = 60 * second
const hour = 60 * minute
const day = 24 * hour
const daysInWeek = 7

export enum Milliseconds {
  SECOND = second,
  MINUTE = minute,
  HOUR = hour,
  DAY = day,
}

export type EnabledDisabledType = 'ENABLED' | 'DISABLED'
export const EnabledDisabled: { [k in EnabledDisabledType]: EnabledDisabledType } = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
}

export type NumberRange = [from: number, to: number]
/**
 * @function getOverlapLength gets the number length overlap between two number ranges, returns 0 when the internal max min give -negative which indicates no overlap
 * @param param0
 * @returns
 */
export const getOverlapLength = ([referenceRange, comparedRange]: [NumberRange, NumberRange]) =>
  Math.max(0, Math.min(referenceRange[1], comparedRange[1]) - Math.max(referenceRange[0], comparedRange[0]))

export const getEnumShape = (arr: string[]): { [k: string]: string } => {
  return arr.reduce(
    (acc, curr) => {
      acc[curr] = curr
      return acc
    },
    {} as { [k: string]: string },
  )
}

export type TimePeriod = { fromDateMs: number; toDateMs: number }

/** checks timestamps are numbers and in asc order */
export const isTimePeriod = (x: any): x is TimePeriod => {
  const c = x as TimePeriod
  return !!c && isNonNaNNumber(c.fromDateMs) && isNonNaNNumber(c.toDateMs) && c.fromDateMs <= c.toDateMs
}

export enum DataLifeCyclePeriod {
  DEVICE_PERIOD = 'DEVICE_PERIOD',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
}

export type LegacyProceessEnv = {
  CustomerParameter: string
  EnvironmentParameter: string
  deployedEnvironment: string
}

export type LambdaProcessEnv = {
  AWS_LAMBDA_FUNCTION_NAME: string
  AWS_SESSION_TOKEN: string
  AWS_DEFAULT_REGION: string
}

export type StandardProceessEnv = {
  AWS_DEFAULT_REGION: string
  AWS_PROFILE: string
  accountId: string
  projectCode: string
  projectName: string
  stage: string
}

export type ReactAppProcessEnv = {
  NODE_ENV: string
  REACT_APP_CURRENT_GIT_SHA: string
  REACT_APP_AWS_ACCOUNT_ID: string
  REACT_APP_DEPLOYMENT_STAGE: string
  REACT_APP_PROJECT_CODE: string
  REACT_APP_PROJECT_NAME: string
  REACT_APP_AWS_DEFAULT_REGION: string
  REACT_APP_IS_SUDO: string
}

export enum FEATURE_ON_OFF {
  ON = 'on',
  OFF = 'off',
}

export enum AppValueTypes {
  NUMBER = 'number',
  DATE = 'date',
  STRING = 'string',
  BOOLEAN = 'boolean',
  ENUM = 'enum',
  TIMESTAMP_MS = 'TIMESTAMP_MS',
  TIMESTAMP_SEC = 'TIMESTAMP_SEC',
  TIMESTAMP_STRING_SEC = 'TIMESTAMP_STRING_SEC',
  OBJECT = 'object',
}

export enum TimestampType {
  STRING,
  NUMBER,
}

export enum GraphType {
  NODE = 'NODE',
  EDGE = 'EDGE',
}

export enum AdjacentType {
  PARENT = 'matchedRelatedByPk',
  CHILD = 'matchedRelatedBySk',
  SUBJECT = 'matchedPrimary',
}

export interface GraphPath {
  steps: GraphStep[]
}

export interface GraphStep {
  objectTypeId: string
  adjacencyType?: AdjacentType.CHILD | AdjacentType.PARENT
}

export type NotificationContent = {
  title: string
  htmlContent: string
  textContent: string
}

export enum CRUD {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export type stageType = 'dev' | 'test' | 'prod'

export enum HttpStatus {
  OK = 200,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  NOT_FOUND = 404,
  METHOD_NOT_ALLOWED = 405,
  NOT_ACCEPTABLE = 406,
  INTERNAL_SERVER_ERROR = 500,
}

/** used in cdk defined api gateways */
export type APIRequestTemplatContextType = {
  stage: string
  request_id: string
  api_id: string
  resource_path: string
  resource_id: string
  http_method: string
  source_ip: string
  account_id: string
  'user-agent': string
  api_key: string
  caller: string
  user: string
  user_arn: string
  princialId: string
  domain: string
  domainPrefix: string
  cognitoIdentiyId: string
}

export type POJOType = void | Date | null | boolean | number | string | POJOType[] | { [prop: string]: POJOType }

export type POJOTypeCompatible<T> = {
  [P in keyof T]: T[P] extends POJOType
    ? T[P]
    : Pick<T, P> extends Required<Pick<T, P>>
      ? never
      : T[P] extends (() => any) | undefined
        ? never
        : POJOTypeCompatible<T[P]>
}

export type googleSheetRef = {
  spreadsheetName?: string
  sheetName?: string
}

export type notificationContentMatrixType = {
  [k: string]: {
    unit: string
    content: string
    link?: string
  }
}

/** Checks NON UI process.envs for completeness, this can only be used in code that does not run in React UIs */
export const checkStdProcessEnvs = () => {
  const { projectCode, stage, AWS_DEFAULT_REGION, accountId, projectName, AWS_PROFILE } = process.env as StandardProceessEnv
  const undefinedEnvVars = Object.entries({ projectCode, stage, AWS_DEFAULT_REGION, accountId, projectName, AWS_PROFILE }).reduce((a, [ck, cv]) => {
    if (!(typeof cv === 'string')) a.push(ck)
    return a
  }, [] as string[])
  if (undefinedEnvVars.length > 0) {
    throw new Error(`The following environment variables are not set ${undefinedEnvVars.join(', ')}`)
  } else {
    console.log({ projectCode, stage, AWS_DEFAULT_REGION, accountId, projectName, AWS_PROFILE })
  }
}

/** Common enforceable regex
 * must be used like: new RegExp(CommonRegexMap.IoTV.eui)
 */
export const CommonRegexMap = {
  LoRa: {
    eui: /^([0-9a-fA-F]{2}){8}$/,
    appKey: /^([0-9a-fA-F]{2}){16}$/,
  },
  IoTV: {
    eui: /^([0-9A-F]{2}){8}$/,
  },
  UUID: {
    v4: /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/,
  },
}

/** test for 8 byte hex */
export const isValidLoRaEui = (s: string | undefined) => !!s && new RegExp(CommonRegexMap.LoRa.eui).test(s);
/** test for 16 byte hex */
export const isValidLoRaAppKey = (s: string | undefined) => !!s && new RegExp(CommonRegexMap.LoRa.appKey).test(s);
/** test for 8 byte uppercase hex */
export const isValidIoTVeui = (s: string | undefined) => !!s && new RegExp(CommonRegexMap.IoTV.eui).test(s);
export const isValidUUIDv4 = (s: string | undefined) => !!s && new RegExp(CommonRegexMap.UUID.v4).test(s);
